<template>
  <b-dropdown
    ref="dropdown"
    block
    no-caret
    variant="link"
    :disabled="loading"
    toggle-class="project-selector__toggle"
    menu-class="project-selector__menu"
    class="project-selector"
    :class="extraClass"
  >
    <template #button-content>
      <span class="project-selector__icon">
        <feather-icon
          icon="HomeIcon"
          size="16"
        />
      </span>
      <span class="project-selector__current">
        <span class="project-selector__label">Obra</span>
        <span class="project-selector__name">{{ currentName }}</span>
      </span>
      <feather-icon
        icon="ChevronDownIcon"
        size="16"
        class="project-selector__chevron"
      />
    </template>

    <b-dropdown-form
      v-if="showSearch"
      class="project-selector__search"
      @submit.stop.prevent
    >
      <b-form-input
        v-model.trim="search"
        size="sm"
        placeholder="Buscar obra"
        autocomplete="off"
      />
    </b-dropdown-form>

    <b-dropdown-item
      v-for="project in filteredProjects"
      :key="project.id"
      :active="project.id === currentProjectId"
      @click="changeProject(project.id)"
    >
      <span class="project-selector__option">
        <span class="project-selector__option-name">{{ project.name }}</span>
        <feather-icon
          v-if="project.id === currentProjectId"
          icon="CheckIcon"
          size="14"
        />
      </span>
    </b-dropdown-item>

    <b-dropdown-text
      v-if="!filteredProjects.length"
      class="project-selector__empty"
    >
      {{ projects.length ? 'Ninguna obra coincide' : 'Todavía no tienes obras' }}
    </b-dropdown-text>
  </b-dropdown>
</template>

<script>
import { ref, computed } from '@vue/composition-api'
import router from '@/router'

// A partir de aqui la lista deja de leerse de un vistazo y conviene poder buscar.
const SEARCHABLE_FROM = 7

export default {
  props: {
    size: {
      type: String,
      default() {
        return 'sm'
      },
    },
    extraClass: {
      type: String,
      default() {
        return ''
      },
    },
  },

  setup(_, { root }) {
    const store = root.$store
    const currentUser = computed(() => store.state.auth.currentUser)
    const projects = ref([])
    const loading = ref(false)
    const search = ref('')

    // Se lee de la ruta y no de una copia: asi el selector sigue diciendo la verdad cuando
    // se llega a otra obra por un enlace y no por este menu.
    const currentProjectId = computed(() => root.$route.params.projectId)

    const currentProject = computed(() => projects.value.find(
      project => project.id === currentProjectId.value,
    ) || store.getters['projects/project'])

    const currentName = computed(() => currentProject.value?.name || 'Elige una obra')

    const showSearch = computed(() => projects.value.length >= SEARCHABLE_FROM)

    const filteredProjects = computed(() => {
      const term = search.value.toLowerCase()
      if (!term) return projects.value

      return projects.value.filter(project => (project.name || '').toLowerCase().includes(term))
    })

    function changeProject(id) {
      search.value = ''
      if (id === currentProjectId.value) return

      loading.value = true
      const selectedProject = projects.value.find(project => project.id === id)
      store.commit('projects/SET_PROJECT', selectedProject)

      router
        .push({ name: router.currentRoute.name, params: { projectId: id } })
        .catch(() => {})
        .finally(() => {
          loading.value = false
        })
    }

    const customerId = currentUser.value?.scoped_roles?.[0]?.role_resource_id
    if (customerId) {
      store
        .dispatch('projects/fetchProjects', { customerId, status: 'true' })
        .then(response => {
          projects.value = response.data
        })
    }

    return {
      projects,
      filteredProjects,
      currentProjectId,
      currentName,
      showSearch,
      search,
      loading,
      changeProject,
    }
  },
}
</script>

<style lang="scss">
.project-selector .project-selector__toggle {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.55rem 0.75rem;
  border-radius: 0.6rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #ffffff;
  text-align: left;
  text-decoration: none;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.project-selector .project-selector__toggle:hover,
.project-selector.show .project-selector__toggle {
  background: rgba(255, 255, 255, 0.18);
  border-color: rgba(255, 255, 255, 0.35);
  color: #ffffff;
}

.project-selector .project-selector__toggle:focus {
  box-shadow: none;
}

.project-selector__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.85rem;
  height: 1.85rem;
  flex-shrink: 0;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.16);
}

.project-selector__current {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex-grow: 1;
  line-height: 1.2;
}

.project-selector__label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.65;
}

.project-selector__name {
  font-size: 0.92rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-selector__chevron {
  flex-shrink: 0;
  opacity: 0.75;
  transition: transform 0.15s ease;
}

.project-selector.show .project-selector__chevron {
  transform: rotate(180deg);
}

.project-selector .project-selector__menu {
  width: 100%;
  padding: 0.35rem;
  border: none;
  border-radius: 0.7rem;
  box-shadow: 0 8px 28px rgba(34, 41, 47, 0.22);
  max-height: 20rem;
  overflow-y: auto;
}

.project-selector .project-selector__search {
  padding: 0.25rem 0.35rem 0.5rem;
}

.project-selector .project-selector__menu .dropdown-item {
  border-radius: 0.45rem;
  padding: 0.5rem 0.65rem;
  white-space: normal;
}

.project-selector .project-selector__menu .dropdown-item.active {
  background: rgba(52, 6, 104, 0.08);
  color: #340668;
  font-weight: 600;
}

.project-selector__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.project-selector__option-name {
  min-width: 0;
  word-break: break-word;
}

.project-selector__empty {
  font-size: 0.8rem;
  color: #6e6b7b;
  padding: 0.5rem 0.65rem;
  margin: 0;
}
</style>
